<template>
  <div class="document">
    <h1>BookDirect.com Privacy Policy</h1>
    <p><strong>Last Updated:</strong> March 10, 2025</p>
    <p>Welcome to <strong>BookDirect.com</strong>. Your privacy is important to us. This Privacy Policy explains how we collect, use, share, and protect your personal data when you access or use our platform. By using our services, you acknowledge and agree to the practices outlined in this policy.</p>

    <section>
      <h2>1. Information We Collect</h2>
      <p>We collect and process the following types of information:</p>
    </section>

    <section>
      <h2>1.1 Personal Information</h2>
      <p>When you make a booking, register an account, or contact our support team, we may collect:</p>
      <ul>
        <li><strong>Identity Data</strong>: Full name, date of birth, nationality.</li>
        <li><strong>Contact Information</strong>: Email address, phone number, mailing address.</li>
        <li><strong>Payment Information</strong>: Credit/debit card details, billing information.</li>
        <li><strong>Booking Details</strong>: Reservation history, preferred accommodations, and special requests.</li>
      </ul>
    </section>

    <section>
      <h3>1.2 Usage and Device Data</h3>
      <p>When you access our platform, we may automatically collect:</p>
      <ul>
        <li><strong>IP address, device type, operating system, and browser type.</strong></li>
        <li><strong>Geolocation data</strong> (if location services are enabled).</li>
        <li><strong>Website interaction data</strong>, such as pages visited, time spent, and referral sources.</li>
      </ul>
    </section>

    <section>
      <h3>1.3 Cookies and Tracking Technologies</h3>
      <p>We use <strong>cookies, tracking pixels, and similar technologies to</strong>:</p>
      <ul>
        <li>Enhance user experience and website functionality.</li>
        <li>Personalize content and marketing communications.</li>
        <li>Analyze website traffic and user behavior.</li>
      </ul>
      <p>You can manage your cookie preferences through your browser settings. See our Cookie Policy below for more details.</p>
    </section>

    <section>
      <h2>2. How We Use Your Information</h2>
      <p>We process your data for the following legitimate purposes:</p>
      <ul>
        <li><strong>To process and manage bookings</strong> and provide customer support.</li>
        <li><strong>To improve and personalize</strong> your experience on our platform.</li>
        <li><strong>To communicate with you</strong> about your reservations, promotions, and service updates.</li>
        <li><strong>To comply with legal obligations</strong>, including fraud prevention, security monitoring, and regulatory compliance..</li>
      </ul>
      <h2>Legal Basis for Processing (GDPR Compliance)</h2>
      <p>We process your data based on the following legal grounds:</p>
      <ul>
        <li><strong>Contractual necessity</strong>: To fulfill booking transactions.</li>
        <li><strong>Legitimate interest</strong>: For website optimization, fraud prevention, and service improvements.</li>
        <li><strong>Legal obligation</strong>: To comply with legal and regulatory requirements.</li>
        <li><strong>Consent</strong>: Where required, such as marketing communications.</li>
      </ul>
    </section>

    <section>
      <h2>3. Data Sharing and Disclosure</h2>
      <p>We do <strong>not</strong> sell your personal data. However, we may share your information with:</p>
    </section>

    <section>
      <h2>3.1 Accommodation Providers</h2>
      <p>When you book accommodations through our platform, we share the necessary details with the <strong>relevant hotel or accommodation provider</strong> to process your reservation.</p>
    </section>

    <section>
      <h2>3.2 Third-Party Service Providers</h2>
      <p>We use trusted third parties for:</p>
      <ul>
        <li><strong>Payment processing</strong> (e.g., Stripe, PayPal).</li>
        <li><strong>Fraud detection and security monitoring</strong>.</li>
        <li><strong>Analytics and advertising</strong> (e.g., Google Analytics, Meta Ads).</li>
        <li><strong>Customer support services</strong>.</li>
      </ul>
      <p>All third-party providers are bound by strict <strong>confidentiality agreements</strong> and data protection laws.</p>
    </section>

    <section>
      <h2>3.3 Legal and Regulatory Compliance</h2>
      <p>We may disclose your data if required by law, legal proceedings, or to protect the rights, safety, or property of <strong>BookDirect.com, users, or third parties</strong>.</p>
    </section>

    <section>
      <h2>4. Data Security Measures</h2>
      <p>We implement <strong>industry-standard security protocols</strong> to protect your personal data, including:</p>
      <ul>
        <li><strong>Encryption of payment data</strong> using SSL/TLS.</li>
        <li><strong>Access control and authentication mechanisms</strong>.</li>
        <li><strong>Regular security audits and compliance reviews</strong>.</li>
      </ul>

      <p><strong>⚠️ No online system is 100% secure.</strong> While we take all reasonable measures, we cannot guarantee absolute security.</p>
    </section>

    <section>
      <h2>5. Data Retention Policy</h2>
      <p>We retain your personal data only for as long as necessary to:</p>
      <ul>
        <li>Fulfill the purposes outlined in this Privacy Policy.</li>
        <li>Comply with legal and regulatory obligations.</li>
        <li>Resolve disputes and enforce agreements.</li>
      </ul>
      <p>Once no longer needed, your data will be securely deleted or anonymized.</p>
    </section>

    <section>
      <h2>6. Your Rights and Choices</h2>
      <p>Depending on your jurisdiction (e.g., <strong>GDPR, CCPA</strong>), you have the following rights:</p>
    </section>

    <section>
      <h2>6.1 Access and Correction</h2>
      <p>You may request a copy of your personal data and correct inaccuracies.</p>
    </section>

    <section>
      <h2>6.2 Opt-Out of Marketing</h2>
      <p>You can opt out of marketing emails by clicking “unsubscribe” in our emails or adjusting your account settings.</p>
    </section>

    <section>
      <h2>6.3 Data Deletion (Right to Be Forgotten)</h2>
      <p>You may request <strong>account deletion</strong> and removal of your personal data, subject to <strong>legal retention requirements</strong>.</p>
    </section>

    <section>
      <h2>6.4 Cookie Management</h2>
      <p>Adjust your cookie preferences through browser settings or visit our Cookie Policy below.</p>
      <p>To exercise any of these rights, contact us at <strong>privacy@bookdirect.com</strong>.</p>
    </section>



    <section>
      <h2>7. International Data Transfers</h2>
      <p>If you access our services from outside the United States of America, your data may be transferred to and processed in countries with different data protection laws. We ensure adequate safeguards through:</p>
      <ul>
        <li><strong>EU Standard Contractual Clauses (SCCs).</strong></li>
        <li><strong>Data protection agreements with third-party providers.</strong></li>
        <li><strong>Privacy Shield compliance (where applicable).</strong></li>
      </ul>
      <p>By using our platform, you consent to such transfers.</p>
    </section>

    <section>
      <h2>8. Our Approach to Transparency & Privacy</h2>
      <p>To ensure we provide maximum transparency & privacy, we ensure</p>

      <ul>
        <li>Transparency in data collection, usage, and storage.</li>
        <li>User control over personal data and marketing preferences.</li>
        <li>Security protocols to safeguard sensitive user information.</li>
      </ul>
      <p>We regularly review our compliance obligations to align with GDPR, CCPA, and other privacy frameworks.</p>
    </section>

    <section>
      <h2>9. 9. Updates to This Privacy Policy</h2>
      <p>We may update this Privacy Policy periodically. Changes will be posted on this page with the “<strong>Last Updated</strong>” date. Continued use of our services constitutes acceptance of the updated policy.</p>
    </section>

    <section>
      <h2>10. Contact Information</h2>
      <p>If you have questions about this Privacy Policy, you can contact us at:</p>
      <p><strong>Email</strong>: <a href="mailto:privacy@bookdirect.com">privacy@bookdirect.com</a></p>
      <p><strong>Phone</strong>: <a href="tel:+1-416-487-7797">+1-416-487-7797</a></p>
      <p><strong>Address: 8 The Green, STE A, Dover, Delaware 19901, USA</strong></p>
      <p>For data protection inquiries, you may also contact your local data protection authority.</p>
    </section>

    <br>

    <section>
      <h2>BookDirect.com Cookie Policy</h2>
      <p>At <strong>BookDirect.com</strong>, we use cookies and similar tracking technologies to enhance your experience, analyze website traffic, and provide personalized content. This Cookie Policy explains how we use cookies and your rights regarding their usage.</p>
    </section>

    <section>
      <h2>1. What Are Cookies?</h2>
      <p>Cookies are small text files stored on your device when you visit our website. They help us recognize your browser, improve website functionality, and personalize your experience.</p>
    </section>

    <section>
      <h2>2. Types of Cookies We Use</h2>
      <br>
      <div class="types-of-cookies">
        <div class="types-of-cookies__row">
          <div class="types-of-cookies__cell"><strong>Category</strong></div>
          <div class="types-of-cookies__cell"><strong>Purpose</strong></div>
        </div>
        <div class="types-of-cookies__row">
          <div class="types-of-cookies__cell"><strong>Essential Cookies</strong></div>
          <div class="types-of-cookies__cell">Necessary for core website functions, such as security, authentication, and booking processes. These cannot be disabled.</div>
        </div>
        <div class="types-of-cookies__row">
          <div class="types-of-cookies__cell"><strong>Analytics Cookies</strong></div>
          <div class="types-of-cookies__cell">Help us track website performance, user behavior, and improve services (e.g., Google Analytics).</div>
        </div>
        <div class="types-of-cookies__row">
          <div class="types-of-cookies__cell"><strong>Functional Cookies</strong></div>
          <div class="types-of-cookies__cell">Enhance user experience by remembering preferences like language and login details.</div>
        </div>
        <div class="types-of-cookies__row">
          <div class="types-of-cookies__cell"><strong>Marketing Cookies</strong></div>
          <div class="types-of-cookies__cell">Used to deliver relevant ads and track campaign effectiveness across platforms (e.g., Google Ads, Meta).</div>
        </div>
      </div>
    </section>

    <section>
      <h2>3. How We Use Cookies</h2>
      <p>We use cookies to:</p>
      <ul>
        <li>Ensure secure and efficient website operation.</li>
        <li>Understand user preferences and improve usability</li>
        <li>Measure and analyze website traffic</li>
        <li>Provide targeted advertising based on browsing behavior</li>
      </ul>
    </section>

    <section>
      <h2>4. Your Choices: Managing Cookies</h2>
      <p>Under <strong>GDPR (EU)</strong> and <strong>CCPA (California)</strong>, you have the right to</p>
      <ul>
        <li><strong>Accept or Reject Cookies:</strong> Adjust your browser settings to block or delete cookies.</li>
        <li><strong>Withdraw Consent:</strong> Use our <strong>Cookie Preference Center</strong> to update your choices anytime.</li>
        <li><strong>Opt-Out of Targeted Ads:</strong> Manage ad preferences via <a href="https://adssettings.google.com/">Google Ad Settings</a> or
          <a href="https://youradchoices.com/">YourAdChoices</a>.</li>
        <li><strong>Do Not Track (DNT):</strong> While some browsers support DNT signals, our website does not respond to these settings.</li>
      </ul>
    </section>

    <section>
      <h2>5. Third-Party Cookies</h2>
      <p>We may allow trusted third-party services (e.g., Google, Meta, payment processors) to place cookies for analytics, security, and advertising. These third parties comply with <strong>data protection laws</strong> and have their own privacy policies.</p>
    </section>

    <section>
      <h2>6. Updates to This Policy</h2>
      <p>We may update this Cookie Policy from time to time. Changes will be posted on this page with an updated “Last Modified” date.</p>
    </section>

    <section>
      <h2>7. Contact Us</h2>
      <p>For any questions or to exercise your cookie rights, contact us at:</p>
      <p><strong>Email:</strong> privacy@bookdirect.com</p>
      <p><strong>Phone:</strong> +1-416-487-7797</p>
      <p>By continuing to use our site, you consent to the use of cookies as outlined in this policy.</p>
    </section>
  </div>
</template>

<style scoped lang="scss" src="./documents.scss"></style>
<style lang="scss" scoped>
.types-of-cookies {
  max-width: 680px;
  &__row {
    display: flex;
    gap: 1rem;
    margin-bottom: 12px;

    & > div:first-child {
      width: 100px;
      max-width: 100px;
      min-width: 100px
    }

  }
}
</style>
